import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import { cn } from '#/lib/utils';
import {
  alertDialogCompactFooterDividerClassName,
  alertDialogFooterDestructiveDividerClassName,
} from '#/modules/commonComponents/dialog/dialogLayoutTokens';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '#/modules/commonComponents/dialog/AlertDialogPrimitives';

/** 開窗後延遲再接受 Enter／Space，避免掃碼槍殘留 Enter 誤關。 */
const CONFIRM_ENTER_SPACE_ARM_MS = 200;

function isConfirmEnterOrSpaceKey(event) {
  return event.key === 'Enter'
    || event.key === ' '
    || event.code === 'Enter'
    || event.code === 'NumpadEnter'
    || event.code === 'Space';
}

const ConfirmDialog = ({
  open,
  onOpenChange,
  title,
  titleSrOnly = false,
  message,
  confirmText = '確認',
  cancelText = '取消',
  showCancel = true,
  destructive = false,
  closeOnOutsideClick = true,
  closeOnEscape = true,
  confirmOnEnterSpace = false,
  onConfirm,
  onCancel,
}) => {
  const onConfirmRef = useRef(onConfirm);

  useEffect(() => {
    onConfirmRef.current = onConfirm;
  }, [onConfirm]);

  useEffect(() => {
    if (!open || !confirmOnEnterSpace) {
      return undefined;
    }

    let armed = false;
    const armTimer = window.setTimeout(() => {
      armed = true;
      const action = document.querySelector(
        '[data-slot="alert-dialog-action"]',
      );
      action?.focus?.();
    }, CONFIRM_ENTER_SPACE_ARM_MS);

    function handleKeyDown(event) {
      if (!isConfirmEnterOrSpaceKey(event)) {
        return;
      }
      if (event.metaKey || event.ctrlKey || event.altKey) {
        return;
      }
      // 掃碼殘留 Enter：吞掉但尚未 armed 時不關閉
      event.preventDefault();
      event.stopPropagation();
      if (!armed) {
        return;
      }
      onConfirmRef.current?.();
    }

    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      window.clearTimeout(armTimer);
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [open, confirmOnEnterSpace]);

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent
        className="gap-0"
        closeOnOutsideClick={closeOnOutsideClick}
        closeOnEscape={closeOnEscape}
      >
        <AlertDialogHeader
          variant={destructive ? 'destructive' : 'muted'}
          compact={!message}
        >
          <AlertDialogTitle className={cn(titleSrOnly && 'sr-only')}>
            {title}
          </AlertDialogTitle>
          {message ? (
            <AlertDialogDescription>{message}</AlertDialogDescription>
          ) : null}
        </AlertDialogHeader>
        <AlertDialogFooter
          divider="top"
          variant={destructive ? 'destructive' : 'muted'}
          dividerClassName={
            destructive
              ? alertDialogFooterDestructiveDividerClassName
              : alertDialogCompactFooterDividerClassName
          }
        >
          {showCancel ? (
            <AlertDialogCancel onClick={onCancel}>{cancelText}</AlertDialogCancel>
          ) : null}
          <AlertDialogAction
            variant={destructive ? 'destructive' : 'default'}
            onClick={onConfirm}
          >
            {confirmText}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

ConfirmDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  titleSrOnly: PropTypes.bool,
  message: PropTypes.node,
  confirmText: PropTypes.string,
  cancelText: PropTypes.string,
  showCancel: PropTypes.bool,
  destructive: PropTypes.bool,
  closeOnOutsideClick: PropTypes.bool,
  closeOnEscape: PropTypes.bool,
  confirmOnEnterSpace: PropTypes.bool,
  onConfirm: PropTypes.func,
  onCancel: PropTypes.func,
};

export default ConfirmDialog;
